import type { IAttentionStatusApi } from '../api/types'
import type { IAttentionOfferingMask, IRaisedAttentionStatus } from '../model/types'

import { applyAttentionStatusOffering } from './applyAttentionStatusOffering'
import { decodeAttentionStatusMask } from './decodeAttentionStatusMask'

interface IDecodeAttentionStatusMasksParams {
  /** The offerings in view and their masks; several of them when the view is `Overall`. */
  masks: IAttentionOfferingMask[]
  catalog: IAttentionStatusApi[]
}

/**
 * The catalog rows a set of offering masks raises, once each, ordered by `SortOrder`.
 *
 * A status two offerings raise is one row: the widget keys its drill-down by `bitValue`, so a
 * duplicate would resolve to the same detail twice. The row then carries both offerings and is
 * titled for neither — `mergeAttentionStatusOfferings` is the same rule the manager cockpit reads
 * across partners.
 */
export const decodeAttentionStatusMasks = ({
  masks,
  catalog,
}: IDecodeAttentionStatusMasksParams): IRaisedAttentionStatus[] => {
  const rawTitles = new Map(catalog.map(({ BitValue, Title }) => [BitValue, Title]))
  const byBitValue = new Map<number, IRaisedAttentionStatus>()

  masks.forEach(({ offering, mask }) =>
    decodeAttentionStatusMask({ mask, catalog, offering }).forEach((status) => {
      const raised = byBitValue.get(status.bitValue)

      if (!raised) {
        byBitValue.set(status.bitValue, status)
        return
      }

      /* A second offering takes the name out of the title: the row now stands for both. */
      raised.offerings = [...new Set([...raised.offerings, ...status.offerings])]
      raised.title = applyAttentionStatusOffering(
        rawTitles.get(status.bitValue) ?? status.title,
        raised.offerings
      )
    })
  )

  return [...byBitValue.values()].sort((left, right) => left.sortOrder - right.sortOrder)
}
